import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import config from '../../app-config'
import {
  create_new_ministry,
  get_ministry_by_id,
  update_ministry,
  delete_ministry,
  fetch_ministry_servers,
  create_ministry_server,
  update_ministry_server,
  delete_ministry_server
} from '../../api/ministryAPI'
import 'react-toastify/dist/ReactToastify.css';
import AdminLayout from '../../layouts/AdminLayout'
import Select from 'react-select'
import notify from '../../utils/IANotification';
import FileUploadField from '../../components/FileUploadField';
import { captialise_first_letter } from '../../utils/helper';
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import ModalLayout from '../../layouts/ModalLayout';
import { MdAdd, MdEdit, MdDelete, MdArrowBack, MdSave, MdPerson } from 'react-icons/md';
import Switch from 'react-switch';

export default function MinistryFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  let user_data = useFetchUserData()

  let ministry_id = new URLSearchParams(window.location.search).get("ministry_id")

  const [activeTab, setActiveTab] = useState("general")
  const [ministryFormData, setMinistryFormData] = useState({
    name: "",
    description: "",
    poc_user_id: "",
    tagline: "",
    ministry_category_id: "",
    link: "",
    parent_ministry_id: null,
    type: "connect",
  })

  const ministry_type = ["connect", "equip", "release"]
  const [users, setUsers] = useState([])
  const [allMinistries, setAllMinistries] = useState([])
  const [serversList, setServersList] = useState([])
  const [, setLoading] = useState(true)

  // Server Modal State
  const [showServerModal, setShowServerModal] = useState(false)
  const [editingServer, setEditingServer] = useState(null)
  const [serverFormData, setServerFormData] = useState({
    user_id: "",
    personnel_name: "",
    role: "Ministry Member",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    is_current: "1"
  })

  const tabs = [
    { id: 'general', label: 'General Information' },
    { id: 'current_servers', label: 'Current Servers' },
    { id: 'past_servers', label: 'Past Servers' },
  ]

  const fetchAllMinistries = async () => {
    try {
      let response = await fetch(config.endpoint + "/ministry/", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      let data = await response.json()
      if (data.status === 200) {
        setAllMinistries(data.data || [])
      }
    } catch (e) {
      console.error(e)
    }
  }

  const fetchPlatformUsers = async () => {
    setLoading(true)
    try {
      let response = await fetch(config.endpoint + "/users/", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })
      let data = await response.json()
      if (data.status === 200) {
        setUsers(data.data || [])
      }
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }

  const fetchMinistryById = useCallback(async () => {
    if (!ministry_id || ministry_id === "new") return
    const data = await get_ministry_by_id(ministry_id)
    if (data?.status === 200) {
      setMinistryFormData(data.data)
    }
  }, [ministry_id])

  const fetchServers = useCallback(async () => {
    if (!ministry_id || ministry_id === "new") return
    const { data, error } = await fetch_ministry_servers(ministry_id)
    if (!error && Array.isArray(data)) {
      setServersList(data)
    }
  }, [ministry_id])

  useEffect(() => {
    fetchMinistryById()
    fetchPlatformUsers()
    fetchAllMinistries()
    fetchServers()
  }, [fetchMinistryById, fetchServers])

  const saveMinistry = async () => {
    const data = await create_new_ministry(ministryFormData)
    if (data?.status === 200) {
      navigate("/admin/ministry/details?ministry_id=" + data.data.id)
      notify({ type: "success", message: "Ministry created successfully" })
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const updateMinistry = async () => {
    const data = await update_ministry(ministry_id, ministryFormData)
    if (data?.status === 200) {
      notify({ type: "success", message: "Ministry updated successfully" })
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const deleteMinistry = async () => {
    const data = await delete_ministry(ministry_id)
    if (data?.status === 200) {
      notify({ type: "success", message: "Ministry deleted" })
      navigate('/admin/ministry')
    } else {
      notify({ type: "error", message: data?.message })
    }
  }

  const handleSaveServer = async () => {
    if (!ministry_id || ministry_id === "new") {
      return notify({ type: "error", message: "Please save the ministry general details first." })
    }

    if (editingServer?.id) {
      const { error } = await update_ministry_server(editingServer.id, serverFormData)
      if (!error) {
        notify({ type: "success", message: "Ministry server updated" })
        setShowServerModal(false)
        fetchServers()
      } else {
        notify({ type: "error", message: "Failed to update server" })
      }
    } else {
      const { error } = await create_ministry_server({
        ...serverFormData,
        ministry_id
      })
      if (!error) {
        notify({ type: "success", message: "Ministry server added" })
        setShowServerModal(false)
        fetchServers()
      } else {
        notify({ type: "error", message: "Failed to add server" })
      }
    }
  }

  const handleDeleteServer = async (id) => {
    const { error } = await delete_ministry_server(id)
    if (!error) {
      notify({ type: "success", message: "Ministry server record removed" })
      fetchServers()
    } else {
      notify({ type: "error", message: "Failed to remove server record" })
    }
  }

  const currentServers = serversList.filter(s => {
    if (s.is_current === "0") return false
    if (s.end_date && new Date(s.end_date) < new Date()) return false
    return true
  })

  const pastServers = serversList.filter(s => {
    if (s.is_current === "0") return true
    if (s.end_date && new Date(s.end_date) < new Date()) return true
    return false
  })

  return (
    <AdminLayout
      navSelected={"Ministry"}
      headerText={ministry_id === "new" ? "New Ministry" : "Ministry Details"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/ministry')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {ministry_id === "new" ? "Create Ministry" : ministryFormData.name || "Ministry Details"}
            </h1>
          </div>
          <div className='flex gap-2'>
            {ministry_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteMinistry}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (ministry_id === "new") saveMinistry()
              else updateMinistry()
            }}>
              {ministry_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      {/* SubNavbar */}
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Add / Edit Server Modal */}
      <ModalLayout modalTitle={editingServer ? "Edit Ministry Server" : "Assign Ministry Server"} isVisible={showServerModal} setIsVisible={setShowServerModal}>
        <div className="space-y-4">
          <div className="input-container">
            <label className="input-label font-semibold">Assign Registered User</label>
            <Select
              isClearable
              placeholder="Select registered user..."
              options={users.map(u => ({
                value: String(u.id),
                label: `${u.first_name || ''} ${u.last_name || ''} (${u.email || u.mobile_phone || u.id})`.trim()
              }))}
              value={users.map(u => ({
                value: String(u.id),
                label: `${u.first_name || ''} ${u.last_name || ''} (${u.email || u.mobile_phone || u.id})`.trim()
              })).find(u => u.value === String(serverFormData.user_id))}
              onChange={(selected) => {
                setServerFormData({
                  ...serverFormData,
                  user_id: selected ? selected.value : "",
                  personnel_name: selected && !serverFormData.personnel_name ? selected.label.split('(')[0].trim() : serverFormData.personnel_name
                })
              }}
            />
          </div>

          <div className="input-container">
            <label className="input-label font-semibold">Or Enter Manual Personnel Name</label>
            <input
              type="text"
              className="text-input"
              value={serverFormData.personnel_name || ""}
              onChange={(e) => setServerFormData({ ...serverFormData, personnel_name: e.target.value })}
              placeholder="e.g. John Tan"
            />
          </div>

          <div className="input-container">
            <label className="input-label font-semibold">Server Duty Role</label>
            <input
              type="text"
              className="text-input"
              value={serverFormData.role || ""}
              onChange={(e) => setServerFormData({ ...serverFormData, role: e.target.value })}
              placeholder="e.g. Ministry Head, Volunteer, Exco, Treasurer"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="input-container">
              <label className="input-label font-semibold">Service Start Date</label>
              <input
                type="date"
                className="text-input"
                value={serverFormData.start_date || ""}
                onChange={(e) => setServerFormData({ ...serverFormData, start_date: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Service End Date (Optional)</label>
              <input
                type="date"
                className="text-input"
                value={serverFormData.end_date || ""}
                onChange={(e) => setServerFormData({ ...serverFormData, end_date: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 bg-gray-50 p-3 rounded-xl border border-gray-200">
            <Switch
              checked={serverFormData.is_current === "1"}
              onChange={(checked) => setServerFormData({ ...serverFormData, is_current: checked ? "1" : "0" })}
            />
            <span className="text-xs font-semibold text-gray-700">
              Active / Current Server Status
            </span>
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="primary" onClick={handleSaveServer}>
              {editingServer ? "Update Server Record" : "Add Server Record"}
            </Button>
          </div>
        </div>
      </ModalLayout>

      {/* TAB 1: GENERAL INFORMATION */}
      {activeTab === "general" && (
        <Card title="Ministry Profile &amp; Governance" subtitle="Configure ministry title, point of contact, type, and description.">
          {ministry_id !== "new" && (
            <div className="mb-6">
              <FileUploadField
                upload_url={config.endpoint + "/file/ministry/id/" + ministry_id}
                max_files={1}
                text={"Upload Ministry Graphic"}
                subtext={"Upload header image for ministry profile page"}
                onChange={() => {}}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="input-container">
              <label className="input-label font-semibold">Ministry Name</label>
              <input
                type="text"
                placeholder='e.g. Worship Team'
                className="text-input"
                value={ministryFormData.name || ""}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, name: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Point of Contact (POC)</label>
              <Select
                options={users.map((user) => ({
                  value: String(user.id),
                  label: `${user.first_name || ''} ${user.last_name || ''} (${user.email || user.mobile_phone || user.id})`.trim()
                }))}
                value={users.map((user) => ({
                  value: String(user.id),
                  label: `${user.first_name || ''} ${user.last_name || ''} (${user.email || user.mobile_phone || user.id})`.trim()
                })).find((u) => u.value === String(ministryFormData.poc_user_id))}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, poc_user_id: e ? e.value : "" })}
              />
              <p className="text-xs text-gray-500 mt-1">*POC contact information will be displayed on ministry pages.</p>
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Ministry Pillar / Type</label>
              <Select
                options={ministry_type.map((type) => ({
                  value: type,
                  label: captialise_first_letter(type),
                }))}
                value={ministry_type.map((type) => ({
                  value: type,
                  label: captialise_first_letter(type),
                })).find((type) => type.value === ministryFormData.type)}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, type: e ? e.value : "connect" })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Parent Ministry (Hierarchy)</label>
              <Select
                options={[
                  { value: "", label: "None (Top Level Ministry)" },
                  ...allMinistries
                    .filter((m) => String(m.id) !== String(ministry_id))
                    .map((m) => ({ value: String(m.id), label: m.name }))
                ]}
                value={[
                  { value: "", label: "None (Top Level Ministry)" },
                  ...allMinistries.map((m) => ({ value: String(m.id), label: m.name }))
                ].find((option) => String(option.value) === String(ministryFormData.parent_ministry_id || ""))}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, parent_ministry_id: e.value ? parseInt(e.value) : null })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">External Resource Link</label>
              <input
                type="text"
                placeholder='e.g. https://forms.gle/...'
                className="text-input"
                value={ministryFormData.link || ""}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, link: e.target.value })}
              />
            </div>

            <div className="input-container">
              <label className="input-label font-semibold">Tagline</label>
              <input
                type="text"
                placeholder='e.g. Worship in the house of the Lord'
                className="text-input"
                value={ministryFormData.tagline || ""}
                onChange={(e) => setMinistryFormData({ ...ministryFormData, tagline: e.target.value })}
              />
            </div>
          </div>

          <div className="input-container mt-6">
            <label className="input-label font-semibold">Full Ministry Description</label>
            <textarea
              placeholder='Describe the ministry purpose, meeting schedules, and commitments...'
              className="textarea-input h-48"
              value={ministryFormData.description || ""}
              onChange={(e) => setMinistryFormData({ ...ministryFormData, description: e.target.value })}
            />
          </div>
        </Card>
      )}

      {/* TAB 2: CURRENT SERVERS */}
      {activeTab === "current_servers" && (
        <Card
          title="Active Ministry Servers"
          subtitle="View and manage active servers currently appointed to this ministry."
          headerRight={
            <Button
              variant="primary"
              icon={MdAdd}
              onClick={() => {
                setEditingServer(null)
                setServerFormData({
                  user_id: "",
                  personnel_name: "",
                  role: "Ministry Member",
                  start_date: new Date().toISOString().split('T')[0],
                  end_date: "",
                  is_current: "1"
                })
                setShowServerModal(true)
              }}
            >
              Assign Server
            </Button>
          }
        >
          {currentServers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <MdPerson size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm font-medium">No active servers currently assigned to this ministry.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentServers.map((s) => {
                const displayName = s.user ? `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim() : (s.personnel_name || "Unassigned Server")
                return (
                  <div key={s.id} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-between gap-4">
                    <div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800 mb-2">
                        {s.role || "Member"}
                      </span>
                      <h4 className="font-bold text-base text-gray-900">{displayName}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Serving since: {s.start_date || "N/A"} {s.end_date ? `(Until ${s.end_date})` : "(Present)"}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" icon={MdEdit} onClick={() => {
                        setEditingServer(s)
                        setServerFormData({
                          user_id: String(s.user_id || ""),
                          personnel_name: s.personnel_name || "",
                          role: s.role || "Ministry Member",
                          start_date: s.start_date || "",
                          end_date: s.end_date || "",
                          is_current: String(s.is_current || "1")
                        })
                        setShowServerModal(true)
                      }}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" icon={MdDelete} onClick={() => handleDeleteServer(s.id)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}

      {/* TAB 3: PAST SERVERS */}
      {activeTab === "past_servers" && (
        <Card
          title="Past Ministry Servers History"
          subtitle="Historical record of servers who previously served in this ministry."
        >
          {pastServers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <MdPerson size={48} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm font-medium">No past server records found for this ministry.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pastServers.map((s) => {
                const displayName = s.user ? `${s.user.first_name || ''} ${s.user.last_name || ''}`.trim() : (s.personnel_name || "Unassigned Server")
                return (
                  <div key={s.id} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-between gap-4">
                    <div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-200 text-gray-700 mb-2">
                        {s.role || "Former Server"}
                      </span>
                      <h4 className="font-bold text-base text-gray-900">{displayName}</h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Service Period: {s.start_date || "N/A"} - {s.end_date || "Ended"}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" icon={MdEdit} onClick={() => {
                        setEditingServer(s)
                        setServerFormData({
                          user_id: String(s.user_id || ""),
                          personnel_name: s.personnel_name || "",
                          role: s.role || "Ministry Member",
                          start_date: s.start_date || "",
                          end_date: s.end_date || "",
                          is_current: String(s.is_current || "0")
                        })
                        setShowServerModal(true)
                      }}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" icon={MdDelete} onClick={() => handleDeleteServer(s.id)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
